import {
  BreastTypeEnum,
  EthnicityEnum,
  EyeColorEnum,
  GenderEnum,
  HairColorEnum,
} from "src/graphql";
import { maxBirthdate, maxDeathdate, partialDateSchema } from "src/utils";
import * as yup from "yup";

const nullCheck = (input: string | null) =>
  input === "" || input === "null" ? null : input;
const zeroCheck = (input: number | null) =>
  input === 0 || Number.isNaN(input) ? null : input;

export const PerformerSchema = yup.object({
  id: yup.string(),
  name: yup.string().trim().required("Name is required"),
  gender: yup
    .string()
    .transform((val: string) => (val === "null" ? null : val))
    .nullable()
    .oneOf([null, ...Object.keys(GenderEnum)], "Gender is required"),
  disambiguation: yup.string().trim().transform(nullCheck).nullable(),
  birthdate: partialDateSchema(maxBirthdate()),
  deathdate: partialDateSchema(maxDeathdate()),
  career_start_year: yup
    .number()
    .transform(zeroCheck)
    .nullable()
    .min(1950, "Invalid year")
    .max(new Date().getFullYear(), "Invalid year"),
  career_end_year: yup
    .number()
    .transform(zeroCheck)
    .min(1950, "Invalid year")
    .max(new Date().getFullYear(), "Invalid year")
    .nullable(),
  height: yup
    .number()
    .transform(zeroCheck)
    .integer("Invalid height, decimals are not allowed")
    .min(100, "Invalid height, Height must be in centimeters.")
    .max(230, "Invalid height")
    .nullable(),
  bandSize: yup
    .number()
    .transform(zeroCheck)
    .min(28, "Size must be 28-56")
    .max(56, "Size must be 28-56")
    .nullable(),
  cupSize: yup
    .string()
    .transform(nullCheck)
    .matches(/^[a-zA-Z]{1,4}$/, {
      excludeEmptyString: true,
      message: "Invalid cup size",
    })
    .nullable(),
  waistSize: yup
    .number()
    .transform(zeroCheck)
    .min(15, "Invalid waist size")
    .max(50, "Invalid waist size")
    .nullable(),
  hipSize: yup.number().transform(zeroCheck).nullable(),
  breastType: yup
    .string()
    .transform(nullCheck)
    .nullable()
    .oneOf([...Object.keys(BreastTypeEnum), null], "Invalid breast type"),
  country: yup.string().trim().transform(nullCheck).nullable().defined(),
  ethnicity: yup
    .string()
    .transform(nullCheck)
    .nullable()
    .oneOf([...Object.keys(EthnicityEnum), null], "Invalid ethnicity"),
  eye_color: yup
    .string()
    .transform(nullCheck)
    .nullable()
    .oneOf([null, ...Object.keys(EyeColorEnum)], "Invalid eye color"),
  hair_color: yup
    .string()
    .transform(nullCheck)
    .nullable()
    .oneOf([null, ...Object.keys(HairColorEnum)], "Invalid hair color"),
  tattoos: yup.array().of(
    yup
      .object({
        location: yup.string().trim().required("Location is required"),
        description: yup.string().trim().transform(nullCheck).nullable(),
      })
      .noUnknown(),
  ),
  piercings: yup.array().of(
    yup
      .object({
        location: yup.string().trim().required("Location is required"),
        description: yup.string().trim().transform(nullCheck).nullable(),
      })
      .noUnknown(),
  ),
  aliases: yup.array().of(yup.string().ensure().trim()).ensure().default([]),
  images: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        url: yup.string().required(),
        width: yup.number().default(0),
        height: yup.number().default(0),
      }),
    )
    .required(),
  urls: yup
    .array()
    .of(
      yup.object({
        url: yup.string().url("Invalid URL").required(),
        site: yup
          .object({
            id: yup.string().required(),
            name: yup.string().required(),
            icon: yup.string().required(),
          })
          .required(),
      }),
    )
    .ensure(),
  note: yup.string().required("Edit note is required"),
});

export type PerformerFormData = yup.Asserts<typeof PerformerSchema>;
